import * as Sentry from '@sentry/browser'

const MB = 1024 * 1024
const CHECK_INTERVAL_MS = 60_000
/** Min time between Sentry warnings for the same threshold. */
const THROTTLE_MS = 15 * 60_000

type MemoryLevel = 'warn' | 'high' | 'critical'

type MemoryThreshold = {
  id: MemoryLevel
  bytes: number
  message: string
}

/** Absolute used-heap thresholds (Chrome/Edge `performance.memory` only). Highest first. */
const THRESHOLDS: MemoryThreshold[] = [
  { id: 'critical', bytes: 3500 * MB, message: 'JS heap exceeded 3500 MB' },
  { id: 'high', bytes: 3000 * MB, message: 'JS heap exceeded 3000 MB' },
  { id: 'warn', bytes: 2000 * MB, message: 'JS heap exceeded 2000 MB' }
]

type PerformanceMemory = {
  usedJSHeapSize: number
  totalJSHeapSize: number
  jsHeapSizeLimit: number
}

function getMemory(): PerformanceMemory | null {
  const perf = performance as Performance & { memory?: PerformanceMemory }
  if (!perf.memory) {
    return null
  }
  return perf.memory
}

function getHighestExceededThreshold(usedBytes: number): MemoryThreshold | null {
  for (const threshold of THRESHOLDS) {
    if (usedBytes >= threshold.bytes) {
      return threshold
    }
  }
  return null
}

/**
 * Polls JS heap usage and reports the highest crossed threshold via Sentry.
 * First crossing per level is ignored; later crossings are throttled (15 min).
 * No-op outside Chromium (no `performance.memory`).
 */
export function startMemoryMonitor(): void {
  if (typeof window === 'undefined' || !getMemory()) {
    return
  }

  const hitCount: Partial<Record<MemoryLevel, number>> = {}
  const lastFiredAt: Partial<Record<MemoryLevel, number>> = {}

  const check = () => {
    const mem = getMemory()
    if (!mem) {
      return
    }

    const threshold = getHighestExceededThreshold(mem.usedJSHeapSize)
    if (!threshold) {
      return
    }

    const count = (hitCount[threshold.id] ?? 0) + 1
    hitCount[threshold.id] = count

    // Ignore the first time this level is the highest exceeded threshold
    if (count < 2) {
      return
    }

    const now = Date.now()
    const last = lastFiredAt[threshold.id] ?? 0
    if (now - last < THROTTLE_MS) {
      return
    }

    lastFiredAt[threshold.id] = now

    Sentry.withScope((scope) => {
      scope.setLevel('warning')
      scope.setTag('memory.level', threshold.id)
      scope.setContext('memory', {
        usedJSHeapSizeMB: Math.round(mem.usedJSHeapSize / MB),
        totalJSHeapSizeMB: Math.round(mem.totalJSHeapSize / MB),
        jsHeapSizeLimitMB: Math.round(mem.jsHeapSizeLimit / MB),
        thresholdMB: Math.round(threshold.bytes / MB),
        path: window.location.pathname
      })
      Sentry.captureMessage(threshold.message)
    })
  }

  check()
  window.setInterval(check, CHECK_INTERVAL_MS)
}
